import * as i1 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, StoreModule } from '@ngrx/store';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import { filter, switchMap, throwError, withLatestFrom, concatMap, map, catchError as catchError$1, of, combineLatest, tap } from 'rxjs';
import { createEntityAdapter } from '@ngrx/entity';
import { UserKinds } from '@usf/user-types/user';
import * as i2 from '@panamax/app-state';
import { catchError, concatMap as concatMap$1, map as map$1 } from 'rxjs/operators';
import * as i1$1 from '@angular/common/http';
import * as i1$2 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { AIListNamespace, AIListEventTypes, AIListEventActions } from '@usf/list-ai-types';
import * as i1$3 from '@panamax/websocket';

const AI_LIST_META_DATA_ACTIONS = {
    load: createAction('[NgRxListAI - ListAI] Load AI List MetaData'),
    loadSuccess: createAction('[NgRxListAI - ListAI] Load AI List MetaData Successfully', props()),
    loadFail: createAction('[NgRxListAI - ListAI] Load AI List MetaData Failed', props()),
};

const AI_LIST_ACTIONS = {
    loadListAi: createAction('[NgRxListAI - ListAI] Load ListAI based on customer'),
    loadListAiSuccess: createAction('[NgRxListAI - ListAI] ListAI loaded Successfully', props()),
    loadListAiFail: createAction('[NgRxListAI - ListAI] Load ListAI Failed', props()),
    create: createAction('[NgRxListAI - ListAI] Create New AI List', props()),
    createFromBestSellingProducts: createAction('[NgRxListAI - ListAI] Create New AI List From Best Selling Products', props()),
    createFromMenuParse: createAction('[NgRxListAI - ListAI] Create New AI List From Menu Parse', props()),
    createSuccess: createAction('[NgRxListAI - ListAI] Create New AI List Succeeded', props()),
    createFailed: createAction('[NgRxListAI - ListAI] Create New AI List Failed', props()),
    setSelectedAIListId: createAction('[NgRxListAI - ListAI] Set Selected Ai List Id', props()),
    aiListUpdatedAsync: createAction('[NgRxListAI - ListAI] Received Websocket notification that an AI List has been updated'),
    loadChanges: createAction('[NgRxListAI - ListAI] Load "My AI Lists" changes'),
    resetStore: createAction('[NgRxListAI - ListAI] Reset AI Lists Store'),
    search: createAction('[NgRxListAI - ListAI] Search Ingredients', props()),
    searchSuccess: createAction('[NgRxListAI - ListAI] Search Ingredients Success', props()),
    searchFail: createAction('[NgRxListAI - ListAI] Search Ingredients Failed', props()),
    searchInList: createAction('[NgRxListAI - ListAI] Search In List', props()),
    updateFilterOptions: createAction('[NgRxListAI - ListAI] Update Filter Options', props()),
    searchMenuResults: createAction('[NgRxListAI - ListAI] Search In Ingredient Menu', props()),
    updateMenuFilterOptions: createAction('[NgRxListAI - ListAI] Update Menu Filter Options', props()),
    parsingPartialFailed: createAction('[NgRxListAI - ListAI] Partial Parsing Failed', props()),
    parsingTotalFailed: createAction('[NgRxListAI - ListAI] Total Parsing Failed'),
};

// eslint-disable-next-line no-shadow
var AIListConstants;
(function (AIListConstants) {
    AIListConstants["beginningOfTimeWatermark"] = "1995-08-26T15:28:17.854Z";
    AIListConstants["featureFlag"] = "split_global_ai_list";
})(AIListConstants || (AIListConstants = {}));

const AI_LIST_KEY = 'ai-list';
const AI_LIST_META_DATA_KEY = 'ai-list-meta-data';

/* eslint-disable no-shadow */
const aIListAdapter = createEntityAdapter({
    selectId: (listAI) => listAI.aiListId,
});
const initialAIListState = aIListAdapter.getInitialState({
    loaded: false,
    loading: false,
    selectedAiListId: '',
    searchResultsState: {
        searchResults: undefined,
        loaded: false,
        loading: false,
    },
    listSearchKey: '',
    filteredItems: [],
    searchError: undefined,
    filterOptions: {
        direct: false,
        earlyCutoff: false,
        discontinuedWhenOut: false,
        exclusiveBrands: false,
    },
    menuSearchSortFilterOptions: {
        searchKey: '',
        filters: [],
    },
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _AIListReducer = createReducer(initialAIListState, on(AI_LIST_ACTIONS.create, (state, action) => aIListAdapter.addOne({
    aiListId: action.options.aiListId,
    aiListName: action.options.aiListName,
    groups: [],
    menuType: action.options.menuType,
}, { ...state, loaded: false, loading: true })), on(AI_LIST_ACTIONS.createSuccess, (state, action) => aIListAdapter.upsertOne(action.aiList, {
    ...state,
    loaded: true,
    loading: false,
})), on(AI_LIST_ACTIONS.createFailed, (state, action) => aIListAdapter.removeOne(action.aiListId, {
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})), on(AI_LIST_ACTIONS.setSelectedAIListId, (state, action) => ({
    ...state,
    selectedAiListId: action.aiListId,
})), on(AI_LIST_ACTIONS.loadListAi, (state) => ({
    ...state,
    loaded: false,
    loading: true,
})), on(AI_LIST_ACTIONS.loadListAiSuccess, (state, action) => aIListAdapter.upsertMany(action.aiLists, {
    ...state,
    loaded: true,
    loading: false,
})), on(AI_LIST_ACTIONS.loadListAiFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
})), on(AI_LIST_ACTIONS.search, (state) => ({
    ...state,
    loaded: false,
    loading: true,
    error: undefined,
    searchResultsState: {
        searchResults: undefined,
        loaded: false,
        loading: true,
    },
})), on(AI_LIST_ACTIONS.searchSuccess, (state, action) => ({
    ...state,
    searchResultsState: {
        searchResults: action.response,
        loaded: true,
        loading: false,
    },
    loaded: true,
    loading: false,
})), on(AI_LIST_ACTIONS.searchFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
    searchResultsState: {
        searchResults: undefined,
        loaded: true,
        loading: false,
    },
})), on(AI_LIST_ACTIONS.searchInList, (state, { searchKey }) => ({
    ...state,
    listSearchKey: searchKey,
})), on(AI_LIST_ACTIONS.updateFilterOptions, (state, { filterOptions }) => ({
    ...state,
    filterOptions,
})), on(AI_LIST_ACTIONS.searchMenuResults, (state, action) => ({
    ...state,
    menuSearchSortFilterOptions: {
        ...state.menuSearchSortFilterOptions,
        searchKey: action.searchKey,
    },
})), on(AI_LIST_ACTIONS.updateMenuFilterOptions, (state, action) => ({
    ...state,
    menuSearchSortFilterOptions: {
        ...state.menuSearchSortFilterOptions,
        filters: action.filters,
    },
})), on(AI_LIST_ACTIONS.resetStore, () => initialAIListState));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function aIListReducer(state, action) {
    return _AIListReducer(state, action);
}

const selectAIListState = createFeatureSelector(AI_LIST_KEY);
const selectSelectedAiList = createSelector(selectAIListState, (aiListState) => aiListState.entities[aiListState.selectedAiListId]);
const selectLatestAiListUpdateDtm = createSelector(selectAIListState, (aiListState) => {
    let latestUpdateDtm = AIListConstants.beginningOfTimeWatermark;
    for (const id of aiListState?.ids) {
        const aiList = aiListState?.entities[id];
        if (new Date(aiList.updateDtm) > new Date(latestUpdateDtm)) {
            latestUpdateDtm = aiList.updateDtm;
        }
    }
    return latestUpdateDtm;
});
const selectSearchResults = createSelector(selectAIListState, (state) => state.searchResultsState);
const { selectIds: selectAiListIds, selectEntities: selectAiListEntities, selectAll: selectAllAiLists, selectTotal: selectTotalAiLists, } = aIListAdapter.getSelectors(selectAIListState);
const selectListSearchKey = createSelector(selectAIListState, (state) => state.listSearchKey);
const selectFilterOptions = createSelector(selectAIListState, (state) => state.filterOptions);
const selectMenuSearchSortFilterOptions = createSelector(selectAIListState, (state) => state.menuSearchSortFilterOptions);

const AI_LIST_ENDPOINT = 'aiList';
const AI_LIST_META_DATA_ENDPOINT = 'aiListMetadata';
const BEST_SELLING_PIM_GROUPS = '/bestSellingPimGroups';
const AI_LIST_MENU_POINT = '/menuParse';
const MY_CHANGES = '/myChanges';
const SEARCH = '/search';

const selectAIListMetaDataState = createFeatureSelector(AI_LIST_META_DATA_KEY);
const selectSortedMenuTypes = createSelector(selectAIListMetaDataState, (aiListMetaDataState) => {
    const menuTypes = convertOptionsToKeyValueArray(aiListMetaDataState.menuTypes ?? {});
    return sortKeyValueAlphabetically(menuTypes);
});
const selectSortedMenuProductTypes = createSelector(selectAIListMetaDataState, (aiListMetaDataState) => {
    const menuProductTypes = convertOptionsToKeyValueArray(aiListMetaDataState.menuProductTypes ?? {});
    return sortKeyValueReverseAlphabetically(menuProductTypes);
});
const selectSortedInputTypes = createSelector(selectAIListMetaDataState, (aiListMetaDataState) => {
    const inputTypes = convertOptionsToKeyValueArray(aiListMetaDataState.inputTypes ?? {});
    return sortKeyValueReverseAlphabetically(inputTypes);
});
const selectAttachmentLimit = createSelector(selectAIListMetaDataState, (aiListMetaDataState) => aiListMetaDataState?.attachmentLimit);
const selectMaxFileSize = createSelector(selectAIListMetaDataState, (aiListMetaDataState) => aiListMetaDataState?.maxFileSize);
const convertOptionsToKeyValueArray = (options) => {
    const keyValueArr = [];
    for (const key in options) {
        if (Object.prototype.hasOwnProperty.call(options, key)) {
            keyValueArr.push({
                key,
                value: options[key],
            });
        }
    }
    return keyValueArr;
};
const sortKeyValueAlphabetically = (keyValueArr) => keyValueArr.sort((a, b) => {
    const valueA = a?.value?.toLowerCase();
    const valueB = b?.value?.toLowerCase();
    return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
});
const sortKeyValueReverseAlphabetically = (keyValueArr) => keyValueArr.sort((a, b) => {
    const valueA = a?.value?.toLowerCase();
    const valueB = b?.value?.toLowerCase();
    return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
});

class AIListService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
        this.aiListState$ = this.store.select(selectAIListState);
        this.selectedAiList$ = this.store.select(selectSelectedAiList);
        this.searchResults$ = this.store.select(selectSearchResults);
        this.aiListFeatureFlag = this.panAppState.feature$([
            AIListConstants.featureFlag,
        ]);
        this.aiListDataInitialization = this.panAppState.session$.pipe(filter((sessionState) => !sessionState.isGuest && sessionState.userType === UserKinds.Internal), switchMap(() => this.aiListFeatureFlag$));
    }
    get aiListFeatureFlag$() {
        return this.aiListFeatureFlag;
    }
    get aiListDataInitialization$() {
        return this.aiListDataInitialization;
    }
    create(options) {
        this.store.dispatch(AI_LIST_ACTIONS.create({ options }));
    }
    setSelectedAiListId(aiListId) {
        this.store.dispatch(AI_LIST_ACTIONS.setSelectedAIListId({ aiListId }));
    }
    search(request) {
        this.store.dispatch(AI_LIST_ACTIONS.search({ request }));
    }
    initialize() {
        // Should listen for the entirety of the session
        this.aiListDataInitialization$.subscribe((aiListFeatureFlag) => {
            if (aiListFeatureFlag) {
                this.store.dispatch(AI_LIST_ACTIONS.resetStore());
                this.store.dispatch(AI_LIST_ACTIONS.loadListAi());
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListService, deps: [{ token: i1.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i2.PanAppState }]; } });

const NGRX_AI_LIST_LIB_CONFIG = new InjectionToken('NGRX_AI_LIST_LIB_CONFIG');

/* eslint-disable no-shadow */
class AIListHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, queryParams) {
        let url = `${this.config.apiUrl}/${path}`;
        if (queryParams) {
            queryParams.forEach((val, key) => {
                url += url.includes('?') ? `&${key}=${val}` : `?${key}=${val}`;
            });
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    handleError(error) {
        return throwError(error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListHttpClientService, deps: [{ token: NGRX_AI_LIST_LIB_CONFIG }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListHttpClientService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_AI_LIST_LIB_CONFIG]
                }] }, { type: i1$1.HttpClient }]; } });

class AiListMetaDataService {
    constructor(store) {
        this.store = store;
        this.aiListMetaData$ = this.store.select(selectAIListMetaDataState);
        this.menuTypes$ = this.store.select(selectSortedMenuTypes);
        this.menuProductTypes$ = this.store.select(selectSortedMenuProductTypes);
        this.inputTypes$ = this.store.select(selectSortedInputTypes);
        this.attachmentLimit$ = this.store.select(selectAttachmentLimit);
        this.maxFileSize$ = this.store.select(selectMaxFileSize);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiListMetaDataService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiListMetaDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AiListMetaDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const initialAIListMetaDataState = {
    menuTypes: {},
    menuProductTypes: {},
    inputTypes: {},
    attachmentLimit: 6,
    loaded: false,
    loading: false,
    maxFileSize: 10,
};
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
const _AIListMetaDataReducer = createReducer(initialAIListMetaDataState, on(AI_LIST_META_DATA_ACTIONS.load, (state, action) => ({
    ...state,
    loaded: false,
    loading: true,
})), on(AI_LIST_META_DATA_ACTIONS.loadSuccess, (state, action) => ({
    ...state,
    ...action.aiListMetaData,
    loaded: true,
    loading: false,
})), on(AI_LIST_META_DATA_ACTIONS.loadFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error?.message ?? 'Failed to load AiListMetaData',
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function aIListMetaDataReducer(state, action) {
    return _AIListMetaDataReducer(state, action);
}

const sessionsHaveNotChanged = (previousSession, latestSession) => previousSession?.selectedCustomerId === latestSession?.selectedCustomerId &&
    previousSession?.division === latestSession?.division &&
    previousSession?.department === latestSession?.department &&
    previousSession?.ecomUserId === latestSession?.ecomUserId;

class AIListEffects {
    constructor(actions$, httpService, store, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.panAppState = panAppState;
        this.load$ = createEffect(() => this.actions$.pipe(ofType(AI_LIST_ACTIONS.loadListAi), withLatestFrom(this.panAppState.session$), concatMap(([action, previousSession]) => this.httpService.get(AI_LIST_ENDPOINT).pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([aiLists]) => AI_LIST_ACTIONS.loadListAiSuccess({ aiLists })), catchError$1((error) => of(AI_LIST_ACTIONS.loadListAiFail(error)))))));
        this.create$ = createEffect(() => this.actions$.pipe(ofType(AI_LIST_ACTIONS.create), map((action) => {
            if (action.options.isFromBestSellingProducts) {
                return AI_LIST_ACTIONS.createFromBestSellingProducts({
                    request: {
                        aiListId: action.options.aiListId,
                        aiListName: action.options.aiListName,
                        menuType: action.options.menuType,
                        listState: action.options.listType,
                    },
                });
            }
            else if (action.options.typeOfImages !== 'BOX_CASE_IMAGE') {
                return AI_LIST_ACTIONS.createFromMenuParse({
                    request: {
                        aiListId: action.options.aiListId,
                        aiListName: action.options.aiListName,
                        menuType: action.options.menuType,
                        menuProductType: action.options.ingredientPreference,
                        listState: action.options.listType,
                        sourceFiles: action.options.sourceFiles,
                        eb: false,
                        usfDirect: false,
                        jit: false,
                        dwo: false,
                    },
                });
            }
            else {
                // Uncomment when implementing for the case of BOXCASE
                //   return AI_LIST_ACTIONS.createFromBoxCase({
                //     request: {}
                //   })
            }
        })));
        this.createFromBestSellingProducts$ = createEffect(() => this.actions$.pipe(ofType(AI_LIST_ACTIONS.createFromBestSellingProducts), concatMap((action) => this.httpService
            .post(AI_LIST_ENDPOINT + BEST_SELLING_PIM_GROUPS, action.request)
            .pipe(map((aiList) => AI_LIST_ACTIONS.createSuccess({ aiList })), catchError$1((error) => of(AI_LIST_ACTIONS.createFailed({
            aiListId: action.request.aiListId,
            error: error?.message,
        })))))));
        this.search$ = createEffect(() => this.actions$.pipe(ofType(AI_LIST_ACTIONS.search), withLatestFrom(this.panAppState.session$), concatMap(([action, previousSession]) => {
            const queryParams = new Map();
            // Add search parameters
            queryParams.set('ingredientName', action.request.ingredientName);
            queryParams.set('pimInfo', action.request.pimInfo);
            if (action.request.eb !== undefined) {
                queryParams.set('eb', action.request.eb.toString());
            }
            if (action.request.jit !== undefined) {
                queryParams.set('jit', action.request.jit.toString());
            }
            if (action.request.usf_dir !== undefined) {
                queryParams.set('usf_dir', action.request.usf_dir.toString());
            }
            if (action.request.dwo !== undefined) {
                queryParams.set('dwo', action.request.dwo.toString());
            }
            return this.httpService
                .get(AI_LIST_ENDPOINT + SEARCH, queryParams)
                .pipe(withLatestFrom(this.panAppState.session$), filter(([response, session]) => sessionsHaveNotChanged(previousSession, session)), map(([response]) => AI_LIST_ACTIONS.searchSuccess({ response })), catchError$1((error) => of(AI_LIST_ACTIONS.searchFail({
                error: error?.message || 'An error occurred during search',
            }))));
        })));
        this.createFromMenuParse$ = createEffect(() => this.actions$.pipe(ofType(AI_LIST_ACTIONS.createFromMenuParse), concatMap((action) => this.httpService
            .post(AI_LIST_ENDPOINT + AI_LIST_MENU_POINT, action.request)
            .pipe(map((_) => AI_LIST_ACTIONS.createSuccess({
            aiList: {
                aiListId: action.request.aiListId,
                aiListName: action.request.aiListName,
                dwo: action.request.dwo,
                eb: action.request.eb,
                jit: action.request.jit,
                listState: action.request.listState,
                menuProductType: action.request.menuProductType,
                menuType: action.request.menuType,
                parsed: false,
                parseJobStates: action.request.sourceFiles.map((item) => ({
                    jobId: '',
                    jobStatus: 'PROCESSING',
                    message: '',
                })),
                sourceFiles: action.request.sourceFiles,
                usfDirect: action.request.usfDirect,
            },
        })), catchError$1((error) => of(AI_LIST_ACTIONS.createFailed({
            aiListId: action.request.aiListId,
            error: error?.message,
        })))))));
        this.loadAiListChanges$ = createEffect(() => this.actions$.pipe(ofType(AI_LIST_ACTIONS.loadChanges, AI_LIST_ACTIONS.aiListUpdatedAsync), withLatestFrom(this.store.select(selectLatestAiListUpdateDtm), this.store.select(selectSelectedAiList)), concatMap(([action, latestUpdateDtm, selectedList]) => {
            const watermark = latestUpdateDtm ?? new Date().toISOString();
            return this.httpService
                .get(AI_LIST_ENDPOINT + MY_CHANGES, new Map([['watermark', watermark]]))
                .pipe(map((aiLists) => {
                // Clean up this code. helper function?
                // Only do the partial/full error check for selected list
                const selectedListInResponse = aiLists.find((list) => list.aiListId === selectedList?.aiListId);
                if (selectedListInResponse?.parseJobStates?.length > 0 &&
                    selectedListInResponse.parsed) {
                    const failedJobs = selectedListInResponse.parseJobStates.filter((job) => job.jobStatus === 'FAILED');
                    if (failedJobs.length > 0) {
                        // If all jobs failed, it's a total failure
                        if (failedJobs.length ===
                            selectedListInResponse.parseJobStates.length) {
                            this.store.dispatch(AI_LIST_ACTIONS.parsingTotalFailed());
                        }
                        else {
                            // Collect all failed files that haven't been retried and haven't been shown before
                            const failedFiles = failedJobs
                                .filter((job) => !job.retried)
                                .map((job) => {
                                // Find the matching source file by index
                                const jobIndex = selectedListInResponse.parseJobStates.indexOf(job);
                                if (jobIndex >= 0 &&
                                    jobIndex < selectedListInResponse.sourceFiles.length) {
                                    const fullPath = selectedListInResponse.sourceFiles[jobIndex]
                                        .fileName;
                                    return fullPath.split('/').pop() || fullPath;
                                }
                                return 'Unknown file';
                            })
                                .filter((fileName) => !this.shownFailedFiles.has(fileName));
                            if (failedFiles.length > 0) {
                                // Add the new failed files to the set
                                failedFiles.forEach((fileName) => this.shownFailedFiles.add(fileName));
                                this.store.dispatch(AI_LIST_ACTIONS.parsingPartialFailed({
                                    failedFiles,
                                }));
                            }
                        }
                    }
                }
                return AI_LIST_ACTIONS.loadListAiSuccess({ aiLists });
            }), catchError$1((error) => of(AI_LIST_ACTIONS.loadListAiFail({
                error: error?.message,
            }))));
        })));
        this.shownFailedFiles = new Set();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListEffects, deps: [{ token: i1$2.Actions }, { token: AIListHttpClientService }, { token: i1.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: AIListHttpClientService }, { type: i1.Store }, { type: i2.PanAppState }]; } });

class AIListMetaDataEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.loadAIListMetaData$ = createEffect(() => this.actions$.pipe(ofType(AI_LIST_META_DATA_ACTIONS.load), concatMap$1(() => this.httpService.get(AI_LIST_META_DATA_ENDPOINT).pipe(map$1((aiListMetaData) => AI_LIST_META_DATA_ACTIONS.loadSuccess({
            aiListMetaData,
        })), catchError((error) => of(AI_LIST_META_DATA_ACTIONS.loadFail({ error })))))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListMetaDataEffects, deps: [{ token: i1$2.Actions }, { token: AIListHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListMetaDataEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListMetaDataEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: AIListHttpClientService }]; } });

// eslint-disable max-len
class AIListWebsocketService {
    constructor(webSocketService, store, aiListService) {
        this.webSocketService = webSocketService;
        this.store = store;
        this.aiListService = aiListService;
        this.websocketObserver$ = combineLatest([
            this.webSocketService?.isConnected$,
            this.aiListService.aiListFeatureFlag$,
        ]).pipe(filter(([isConnected, featureFlag]) => featureFlag), tap(([isConnected, featureFlag]) => {
            if (!isConnected) {
                // eslint-disable-next-line no-console
                console.info('%c** ai list domain web socket disconnected. **', 'color: red; font-weight:bold;');
            }
        }), filter(([isConnected, featureFlag]) => isConnected), tap(() => this.webSocketService.register([
            {
                namespace: AIListNamespace.AIListNamespace,
                eventType: [AIListEventTypes.ListEvent],
            },
        ])), switchMap(() => this.webSocketService?.getTopicData()), map((event) => JSON.parse(event?.data)), catchError$1((error) => {
            // eslint-disable-next-line no-console
            console.debug('Error while parsing websocket event');
            return of();
        }), map((websocketEvent) => this.mapWebsocketEventToAction(websocketEvent)));
    }
    initialize() {
        // if there is no websocket service injected, return out
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.webSocketService['baseUrl']) {
            // eslint-disable-next-line no-console
            console.info('%c** no websocket injected for list domain **', 'color: black; font-weight:bold;');
            return;
        }
        // Subscribe to websockets for the whole length of the application
        this.websocketObserver$.subscribe();
    }
    mapWebsocketEventToAction(websocketEvent) {
        if (this.shouldDispatchAiListUpdated(websocketEvent)) {
            this.store.dispatch(AI_LIST_ACTIONS.aiListUpdatedAsync());
        }
    }
    shouldDispatchAiListUpdated(websocketEvent) {
        return (this.isAiListUpdatedEvent(websocketEvent) &&
            this.hasMessageContent(websocketEvent) &&
            this.isAiListNamespace(websocketEvent));
    }
    isAiListUpdatedEvent(websocketEvent) {
        return websocketEvent?.data?.eventAction === AIListEventActions.ListUpdated;
    }
    hasMessageContent(websocketEvent) {
        return websocketEvent?.data?.messageContent?.length > 0;
    }
    isAiListNamespace(websocketEvent) {
        return websocketEvent?.data?.namespace === AIListNamespace.AIListNamespace;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListWebsocketService, deps: [{ token: i1$3.WebSocketService }, { token: i1.Store }, { token: AIListService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListWebsocketService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AIListWebsocketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.WebSocketService }, { type: i1.Store }, { type: AIListService }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
class NgRxAIListModule {
    constructor(ngRxListAILibConfig) {
        this.ngRxListAILibConfig = ngRxListAILibConfig;
    }
    static forRoot(ngRxListAILibConfig) {
        return {
            ngModule: NgRxAIListModule,
            providers: [
                {
                    provide: NGRX_AI_LIST_LIB_CONFIG,
                    useValue: ngRxListAILibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeWebSocket,
                    deps: [AIListWebsocketService],
                    multi: true,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeListState,
                    deps: [AIListService],
                    multi: true,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgRxAIListModule, deps: [{ token: NGRX_AI_LIST_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NgRxAIListModule, imports: [i1.StoreFeatureModule, i1.StoreFeatureModule, i1$2.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgRxAIListModule, providers: [AIListHttpClientService, AiListMetaDataService], imports: [StoreModule.forFeature(AI_LIST_KEY, aIListReducer),
            StoreModule.forFeature(AI_LIST_META_DATA_KEY, aIListMetaDataReducer),
            EffectsModule.forFeature([AIListEffects, AIListMetaDataEffects])] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgRxAIListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(AI_LIST_KEY, aIListReducer),
                        StoreModule.forFeature(AI_LIST_META_DATA_KEY, aIListMetaDataReducer),
                        EffectsModule.forFeature([AIListEffects, AIListMetaDataEffects]),
                    ],
                    providers: [AIListHttpClientService, AiListMetaDataService],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_AI_LIST_LIB_CONFIG]
                }] }]; } });
function initializeListState(listState) {
    const state = () => listState.initialize();
    return state;
}
function initializeWebSocket(webSocketService) {
    const websocket = () => webSocketService.initialize();
    return websocket;
}

/*
 * Public API Surface of ngrx-list-ai
 */
// models

/**
 * Generated bundle index. Do not edit.
 */

export { AIListHttpClientService, AIListService, AI_LIST_ACTIONS, AI_LIST_ENDPOINT, AI_LIST_KEY, AI_LIST_MENU_POINT, AI_LIST_META_DATA_ACTIONS, AI_LIST_META_DATA_ENDPOINT, AI_LIST_META_DATA_KEY, AiListMetaDataService, BEST_SELLING_PIM_GROUPS, MY_CHANGES, NgRxAIListModule, SEARCH, _AIListReducer, aIListAdapter, aIListReducer, initialAIListState, selectAIListMetaDataState, selectAIListState, selectAiListEntities, selectAiListIds, selectAllAiLists, selectAttachmentLimit, selectFilterOptions, selectLatestAiListUpdateDtm, selectListSearchKey, selectMaxFileSize, selectMenuSearchSortFilterOptions, selectSearchResults, selectSelectedAiList, selectSortedInputTypes, selectSortedMenuProductTypes, selectSortedMenuTypes, selectTotalAiLists };
